import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useState, useEffect } from "react";
import { getStaticFileUrl } from "../utils/config";

interface MainVisualContent {
  subtitle: string;
  title: string;
  highlightedText: string;
  description: string;
  backgroundVideo?: string;
}

const MainVisual = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [content, setContent] = useState<MainVisualContent>({
    subtitle: '',
    title: '',
    highlightedText: '',
    description: '',
    backgroundVideo: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 서버에서 Main Visual 데이터 불러오기
    const loadMainVisualData = async () => {
      try {
        const { aboutApi } = await import('../services/api');
        const data = await aboutApi.get();
        if (data && data.mainVisual) {
          setContent(data.mainVisual);
        } else {
          // mainVisual 데이터가 없으면 기본값 사용
          console.warn('mainVisual 데이터가 없습니다. 기본값을 사용합니다.');
          setContent({
            subtitle: '지상파방송ㆍ홍보영상ㆍ마케팅ㆍ공연 기획',
            title: '씨유미디어그룹',
            highlightedText: '재미와 공익을 동시에 충족시키는',
            description: '공중파 프로그램 제작에서 이미 독보적인 영역을 개척하고 한발 더 나아가 차별화된\n다큐멘터리와 기업의 전문적인 홍보영상, 디지털 콘텐츠, 오프라인 관련 사업을 기획 및 제작하고 있습니다.',
            backgroundVideo: '/video/mainvideo.mp4',
          });
        }
      } catch (error) {
        console.error('Main Visual 데이터 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadMainVisualData();
  }, []);

  // 로딩 중이거나 필수 데이터가 없으면 로딩 화면 표시
  if (loading || !content.backgroundVideo) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#000",
        }}
      >
        <Box
          sx={{
            color: "#fff",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">로딩 중...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <source
          src={getStaticFileUrl(content.backgroundVideo)}
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)", // 어두운 반투명 효과
          zIndex: 1,
        }}
      />

      {/* Content Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: isMobile ? "5%" : "20%",
          transform: "translateY(-50%)",
          color: "#fff",
          textAlign: "left",
          maxWidth: isMobile ? "90%" : "1130px",
          zIndex: 2,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: isMobile ? "1.25rem" : "2rem", // 모바일에서 크기 줄이기
            fontWeight: 300,
            lineHeight: isMobile ? 1.2 : 1.3,
            marginBottom: theme.spacing(2),
          }}
        >
          {content.subtitle}
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontSize: isMobile ? "2rem" : "3.4rem", // 모바일에서 크기 줄이기
            fontWeight: 700,
            lineHeight: isMobile ? 1.2 : 1.3,
            marginBottom: theme.spacing(2),
            color: "#fff",
          }}
        >
          <Box component="span" sx={{ color: "#F05555" }}>
            {content.highlightedText}
          </Box>{" "}
          {content.title}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: isMobile ? "1rem" : "1.15rem", // 본문 텍스트 크기 줄이기
            fontWeight: 300,
            lineHeight: isMobile ? 1.5 : 1.8, // 모바일에서 간격 조정
            color: "rgba(255, 255, 255, 0.8)",
            whiteSpace: "pre-line",
          }}
        >
          {content.description}
        </Typography>
      </Box>
    </Box>
  );
};

export default MainVisual;
